import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const MugFrame = styled(motion.div)`
  width: 100%;
  max-width: 1400px;
  height: 90vh;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const MugHeader = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 3px solid #ff6b6b;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 15px;
    min-height: auto;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const MugTitle = styled.h2`
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.2rem;
  }
`;

const ColorDisclaimer = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.7rem;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    justify-content: center;
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const SortLabel = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
`;

const SortSelect = styled.select`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid white;
  border-radius: 15px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const CloseButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid white;
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  transition: all 0.3s ease;
  position: absolute;
  top: 10px;
  right: 15px;
  
  &:hover {
    background: white;
    color: #ff6b6b;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    top: 8px;
    right: 12px;
  }
`;

const MugContent = styled.div`
  width: 100%;
  height: calc(100% - 70px);
  background: #f8f9fa;
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    height: calc(100% - 80px);
    padding: 1rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProductCard = styled(motion.a)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    text-decoration: none;
    color: inherit;
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  
  img {
    width: 95%;
    height: 95%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductTitle = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const ProductDescription = styled.p`
  color: #666;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  line-height: 1.4;
  flex: 1;
`;

const ProductPrice = styled.div`
  color: #ff6b6b;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const AddToCartButton = styled(motion.div)`
  width: 100%;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-decoration: none;
  text-align: center;
  display: block;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
    color: white;
    text-decoration: none;
  }
`;

const MugProducts = [
  {
    id: 1,
    title: "Freakn' Coffee Cup",
    description: "Start your day with a smile! This ceramic mug features the Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage!",
    price: "$12.99",
    image: "/assets/products/mug/white-ceramic-mug-with-color-inside-red-11-oz-front-68a9159f41e0d.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-coffee-cup",
    category: "coffee-mugs"
  },
  {
    id: 2,
    title: "Freakn' Rise & Shine Coffee Mug",
    description: "Start your mornings with attitude! This ceramic coffee mug features the iconic Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage to help you rise and shine!",
    price: "$12.99",
    image: "/assets/products/freaknRiseandShineMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4bfbc48e9a.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-rise-shine-coffee-mug",
    category: "coffee-mugs"
  },
  {
    id: 3,
    title: "Where's My Cluckn' COFFEE! Mug",
    description: "Find your morning motivation with this sassy coffee mug featuring the iconic Chikin Lady design! Perfect for coffee, tea, or any hot beverage to help you find your cluckn' coffee!",
    price: "$12.99",
    image: "/assets/products/wheresmyCluckinCoffeeMug/893291868b4b00441334__825.png",
    buyUrl: "https://chikinlady.printful.me/product/wheres-my-cluckn-coffee-mug",
    category: "coffee-mugs"
  },
  {
    id: 4,
    title: "Karen Chikin 'Cluck Around & Find Out!' Mug",
    description: "Don't mess with this sassy Karen Chikin! This ceramic mug features the iconic 'Cluck Around & Find Out!' design and is microwave safe. Perfect for coffee, tea, or any hot beverage to show off your attitude!",
    price: "$14.99",
    image: "/assets/products/karenMug/white-glossy-mug-white-15-oz-halloween-68b4c5ae989b9.jpg",
    buyUrl: "https://chikinlady.printful.me/product/karen-chikin-cluck-around-find-out-mug",
    category: "coffee-mugs"
  },
  {
    id: 5,
    title: "Stop Cluckn' Around Mug",
    description: "Time to get serious with this sassy coffee mug! This ceramic mug features the iconic 'Stop Cluckn' Around' design and is microwave safe. Perfect for coffee, tea, or any hot beverage to motivate you to stop procrastinating!",
    price: "$12.99",
    image: "/assets/products/stopCluckinaroundMug/893424868b4bcd0c29ad__825.png",
    buyUrl: "https://chikinlady.printful.me/product/stop-cluckn-around-mug",
    category: "coffee-mugs"
  },
  {
    id: 6,
    title: "Chikin Lady Mug",
    description: "The classic Chikin Lady design on a ceramic mug! This microwave-safe mug features the iconic Chikin Lady artwork and is perfect for coffee, tea, or any hot beverage. Show off your love for the original Chikin Lady!",
    price: "$12.99",
    image: "/assets/products/chikinladyMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4c70da7bb1.jpg",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-mug",
    category: "coffee-mugs"
  }
];

const MugOverlay = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredProducts = selectedCategory === 'all' 
    ? MugProducts 
    : MugProducts.filter(product => product.category === selectedCategory);

  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MugFrame
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
          >
            <MugHeader>
              <HeaderContent>
                <MugTitle>☕ Mug Collection</MugTitle>
                <ColorDisclaimer>*Some products have different color options available</ColorDisclaimer>
                <SortContainer>
                  <SortLabel>Sort by:</SortLabel>
                  <SortSelect 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Mugs</option>
                    <option value="coffee-mugs">Coffee Mugs</option>
                  </SortSelect>
                </SortContainer>
              </HeaderContent>
              <CloseButton
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✕ Close
              </CloseButton>
            </MugHeader>
            <MugContent>
              <ProductsGrid>
                {filteredProducts.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    href={product.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ProductImage>
                      <img 
                        src={product.image} 
                        alt={product.title}
                        onError={(e) => {
                          console.error(`Failed to load image: ${product.image}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    </ProductImage>
                    <ProductInfo>
                      <ProductTitle>{product.title}</ProductTitle>
                      <ProductDescription>{product.description}</ProductDescription>
                      <ProductPrice>{product.price}</ProductPrice>
                      <AddToCartButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        🛒 Buy Now
                      </AddToCartButton>
                    </ProductInfo>
                  </ProductCard>
                ))}
              </ProductsGrid>
            </MugContent>
          </MugFrame>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default MugOverlay;
